import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { QuoteModal } from "@/components/QuoteModal";
import { XCircle, Clock, AlertTriangle, FileText, Mail, Phone, CheckCircle } from "lucide-react";

const CancellationPolicy = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />

      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-in">
            <XCircle className="h-16 w-16 text-primary mx-auto mb-6" />
            <h1 className="text-5xl font-bold mb-6">Cancellation Policy</h1>
            <p className="text-xl text-muted-foreground">
              Costal Meridian Logistics - Global Operations
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Last Updated: December 2024
            </p>
          </div>

          {/* Quick Summary */}
          <Card className="mb-12 border-primary">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <AlertTriangle className="h-6 w-6 text-accent" />
                Quick Summary
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="font-semibold">7+ Days Before</p>
                  <p className="text-sm text-muted-foreground">Full refund available</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <Clock className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                  <p className="font-semibold">3-7 Days Before</p>
                  <p className="text-sm text-muted-foreground">50% refund or credit</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <XCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                  <p className="font-semibold">Less than 3 Days</p>
                  <p className="text-sm text-muted-foreground">No refund available</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Policy */}
          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4">1. General Cancellation Terms</h2>
              <p className="text-muted-foreground mb-4">
                Costal Meridian Logistics ("Company," "we," "us") understands that circumstances may require you to cancel or modify your booking. This policy outlines the terms and conditions for cancellations across all our services.
              </p>
              <p className="text-muted-foreground">
                All cancellation requests must be submitted in writing via email to support@costalmeridian.com, or through your client portal account.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. Ocean Freight Cancellations</h2>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4">Full Container Load (FCL)</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>14+ days before vessel departure:</strong> Full refund minus administrative fee (€75 / ฿2,500)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Clock className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <span><strong>7-13 days before departure:</strong> 75% refund or 100% credit for future booking</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Clock className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                      <span><strong>3-6 days before departure:</strong> 50% refund or 75% credit for future booking</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Less than 3 days:</strong> No refund; 25% credit may be considered</span>
                    </li>
                  </ul>

                  <h3 className="font-semibold mt-6 mb-4">Less than Container Load (LCL)</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>7+ days before consolidation:</strong> Full refund minus handling fee (€50 / ฿1,750)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Clock className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <span><strong>3-6 days before:</strong> 50% refund or 75% credit</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Less than 3 days:</strong> No refund available</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. Air Freight Cancellations</h2>
              <Card>
                <CardContent className="p-6">
                  <p className="text-muted-foreground mb-4">
                    Due to the time-sensitive nature of air freight, cancellation terms are more stringent:
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>72+ hours before scheduled pickup:</strong> Full refund minus booking fee (€100 / ฿3,500)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Clock className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <span><strong>24-72 hours before:</strong> 50% refund or 75% credit</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Less than 24 hours:</strong> No refund; cargo already allocated on aircraft</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Container Rental Cancellations</h2>
              <Card>
                <CardContent className="p-6">
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Before delivery:</strong> Full refund of rental deposit minus administrative fee</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Clock className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Within first 7 days of rental:</strong> Pro-rated refund based on unused days</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span><strong>After 7 days:</strong> Minimum one-month rental charge applies</span>
                    </li>
                  </ul>
                  <p className="text-sm text-muted-foreground mt-4">
                    Note: Container purchases are subject to separate terms and a 3-day inspection period.
                  </p>
                </CardContent>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Warehousing & Storage Cancellations</h2>
              <Card>
                <CardContent className="p-6">
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>30+ days notice:</strong> Full refund of prepaid storage minus one week's charges</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Clock className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <span><strong>14-29 days notice:</strong> 50% refund of prepaid storage</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Less than 14 days:</strong> No refund; charges apply until goods removed</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Customs Brokerage Services</h2>
              <p className="text-muted-foreground">
                Customs brokerage fees are non-refundable once documentation work has commenced. If cancellation occurs before any work begins, a full refund minus consultation fee ($50) will be issued. Any third-party charges (government fees, duties, etc.) that have been paid are non-refundable.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Project Cargo & Special Services</h2>
              <p className="text-muted-foreground mb-4">
                Project cargo, heavy lift, and oversized shipments require customized cancellation terms based on the specific project requirements. These terms will be outlined in your service agreement. Generally:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>Equipment mobilization costs are non-refundable once confirmed</li>
                <li>Engineering and survey costs are non-refundable</li>
                <li>Vessel charter cancellations subject to carrier's terms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Force Majeure</h2>
              <p className="text-muted-foreground">
                In cases of force majeure (natural disasters, war, pandemic, government restrictions, etc.), Costal Meridian will work with clients to reschedule shipments at no additional charge or provide full credit for future services. No cancellation fees will apply in documented force majeure situations affecting either USA, origin, destination, or transit countries.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">9. Modifications vs. Cancellations</h2>
              <p className="text-muted-foreground mb-4">
                We encourage modifications over cancellations when possible:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>Date changes (with 7+ days notice): Free of charge, subject to availability</li>
                <li>Route changes: May incur rate differences; no penalty fee</li>
                <li>Volume changes (±10%): Usually accommodated without penalty</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">10. How to Cancel</h2>
              <Card className="bg-secondary">
                <CardContent className="p-6">
                  <div className="max-w-xl mx-auto">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Mail className="h-5 w-5 text-primary" />
                      USA Corporate Headquarters
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Email: support@costalmeridian.com<br />
                      Phone: (714) 798-3650<br />
                      Hours: Mon-Fri, 8:00 AM - 6:00 PM PST
                    </p>
                  </div>
                  <div className="mt-6 pt-6 border-t">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Required Information
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Please include: Booking reference number, reason for cancellation, preferred resolution (refund or credit), and contact details for confirmation.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">11. Refund Processing</h2>
              <p className="text-muted-foreground">
                Approved refunds will be processed within 14 business days to the original payment method. Bank transfers may take additional 3-5 business days depending on your financial institution. Credit notes are valid for 12 months from issue date and can be applied to any Costal Meridian service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">12. Disputes</h2>
              <p className="text-muted-foreground">
                If you believe a cancellation fee has been applied incorrectly, please contact our customer service team within 30 days. All disputes will be reviewed by our management team and resolved within 14 business days. Unresolved disputes may be escalated to arbitration in accordance with our Terms and Conditions.
              </p>
            </section>

            <section className="border-t pt-8">
              <p className="text-sm text-muted-foreground text-center">
                This cancellation policy is part of Costal Meridian Logistics' Terms and Conditions. By booking our services, you agree to these terms. Costal Meridian reserves the right to update this policy with 30 days' notice to existing clients. For questions, contact support@costalmeridian.com.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
      <QuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} />
    </div>
  );
};

export default CancellationPolicy;